/**
 * Documentation vs simple task completion.
 * requiresDocumentation=true → checkbox opens empty note form; submit until documented.
 * requiresDocumentation=false → checkbox sign-off only.
 */

import {
  createEmptyFluidBalanceNote,
  createEmptyMarNote,
} from '../constants/schemas';
import { progressNoteHasContent } from './progressNoteBody';
import { getMedicationRequiredExtraBlocker } from './drugCategory';
import { getCurrentCareTime, normalizeCareTime } from './lateEntry';
import { splitDoseAndRoute, resolveMedicationRoute } from './medicationTaskDose';

export function getDocumentationNoteType(task) {
  if (!task) return 'progress_note';
  if (task.type === 'fluid_balance') return 'fluid_balance';
  if (task.sourceNoteType === 'vitals') return 'vitals';
  if (task.sourceNoteType === 'medication_admin') return 'medication_admin';
  if (task.sourceNoteType === 'progress_note') return 'progress_note';
  if (task.sourceNoteType === 'care_plan') return 'care_plan';

  const text = `${task.title || ''} ${task.itemLabel || ''}`.toLowerCase();
  if (/evaluation|تقييم/.test(text)) return 'care_plan';
  if (/fluid/.test(text)) return 'fluid_balance';
  if (/medication|mar|\bdrug\b|\bdose\b/.test(text)) return 'medication_admin';
  if (/vital|blood pressure|pulse|temp|o2|sugar/.test(text)) return 'vitals';
  return 'progress_note';
}

export function inferRequiresDocumentation(task) {
  if (!task) return false;
  if (typeof task.requiresDocumentation === 'boolean') {
    return task.requiresDocumentation;
  }

  if (task.type === 'fluid_balance') return true;
  if (task.sourceNoteType === 'vitals') return true;
  if (task.sourceNoteType === 'medication_admin') return true;
  if (task.sourceNoteType === 'progress_note') return true;

  const text = `${task.title || ''} ${task.itemLabel || ''}`.trim();
  if (!text) return false;

  if (/pain assessment|pain \(flacc|wong-baker|\bpain\b.*assess|ألم/i.test(text)) return true;
  if (/wound site|wound assessment|موقع الجرح/i.test(text)) return true;
  if (/care plan evaluation|\bevaluation\b|تقييم/i.test(text)) return true;
  if (/fluid balance/i.test(text)) return true;

  return false;
}

export function withRequiresDocumentation(task) {
  if (!task) return task;
  if (typeof task.requiresDocumentation === 'boolean') return task;
  return {
    ...task,
    requiresDocumentation: inferRequiresDocumentation(task),
  };
}

export function taskRequiresDocumentation(task) {
  return Boolean(inferRequiresDocumentation(task));
}

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

export function getManualVisibleVitalsKeys(task) {
  const text = `${task?.itemLabel || ''} ${task?.title || ''}`.toLowerCase();
  if (/blood pressure|\bbp\b|ضغط/.test(text)) return ['bp'];
  if (/pulse|heart rate|نبض/.test(text)) return ['pulse'];
  if (/temp|حرارة/.test(text)) return ['temp_celsius'];
  if (/o2|oxygen|sat|تشبع/.test(text)) return ['o2_sat'];
  if (/sugar|glucose|سكر/.test(text)) return ['blood_sugar'];
  if (/pain|ألم/.test(text)) return ['pain'];
  if (/rr|respirat|تنف/.test(text)) return ['rr'];
  return ['bp', 'pulse', 'temp_celsius', 'o2_sat', 'rr', 'pain', 'blood_sugar'];
}

function parseDoseParts(doseText) {
  const trimmed = String(doseText || '').trim();
  if (!trimmed) {
    return { dose_amount: null, dose_unit: null, dose: '' };
  }
  const match = trimmed.match(/^([\d.]+)\s*(.*)$/);
  if (match) {
    const amount = parseFloat(match[1]);
    const unit = match[2].trim() || null;
    return {
      dose_amount: Number.isNaN(amount) ? null : amount,
      dose_unit: unit,
      dose: trimmed,
    };
  }
  return { dose_amount: null, dose_unit: null, dose: trimmed };
}

function drugNameFromTask(task) {
  if (!task) return '';
  if (task.drug_name) return String(task.drug_name).trim();
  if (task.name && task.sourceNoteType === 'medication_admin') return String(task.name).trim();
  const label = String(task.itemLabel || '').trim();
  if (!label) return String(task.title || 'Medication').trim();
  return label
    .replace(/\s+[\d.]+\s*(mg|g|mcg|µg|ml|units?|iu|tab|capsule)s?\b.*$/i, '')
    .trim() || label;
}

function timeGivenFromTask(task) {
  if (task?.time_given) return normalizeCareTime(task.time_given) || String(task.time_given);
  if (task?.dueTime) {
    const due = new Date(task.dueTime);
    if (!Number.isNaN(due.getTime())) {
      return `${String(due.getHours()).padStart(2, '0')}:${String(due.getMinutes()).padStart(2, '0')}`;
    }
  }
  return getCurrentCareTime();
}

function routeFromTask(task) {
  return resolveMedicationRoute({
    route: task?.route || task?.metadata?.route || '',
    dose: task?.dose || task?.metadata?.dose || '',
    metadata: task?.metadata,
    itemLabel: task?.itemLabel || '',
    title: task?.title || '',
    name: task?.name || task?.drug_name || '',
    drug_name: task?.drug_name || task?.name || '',
  });
}

export function buildMarMedFromTask(task) {
  const drugName = drugNameFromTask(task);
  const rawDose = task?.dose || task?.metadata?.dose || '';
  const split = splitDoseAndRoute(rawDose);
  const doseText = split.dose || rawDose;
  const doseParts = parseDoseParts(doseText);
  const response = task?.patient_response || task?.response || '';
  return {
    drug_name: drugName,
    name: drugName,
    dose: doseParts.dose || doseText || '',
    dose_amount: task?.dose_amount ?? doseParts.dose_amount,
    dose_unit: task?.dose_unit || doseParts.dose_unit,
    route: routeFromTask(task),
    time_given: timeGivenFromTask(task),
    patient_response: response,
    response,
    adverse_reaction: Boolean(task?.adverse_reaction),
    adverse_reaction_details: task?.adverse_reaction_details || null,
    indication: task?.indication || null,
    confidence: 'high',
    pain_before: task?.pain_before ?? null,
    pain_after: task?.pain_after ?? null,
    bp_before: task?.bp_before ?? null,
    bp_systolic_before: task?.bp_systolic_before ?? null,
    bp_diastolic_before: task?.bp_diastolic_before ?? null,
    pulse_before: task?.pulse_before ?? null,
    blood_sugar_before: task?.blood_sugar_before ?? null,
    blood_sugar_after: task?.blood_sugar_after ?? null,
    inr: task?.inr ?? null,
    injection_site: task?.injection_site ?? null,
    infusion_rate: task?.infusion_rate ?? null,
    volume: task?.volume ?? null,
  };
}

export function createEmptyNoteForType(type, task = null) {
  if (type === 'vitals') {
    return {
      note_type: 'vitals',
      vitals: {
        bp_systolic: null,
        bp_diastolic: null,
        pulse: null,
        temp_celsius: null,
        o2_sat: null,
        rr: null,
        pain: null,
        weight_kg: null,
        blood_sugar: null,
      },
      flags: [],
      patient: { name: null, room: null, age: null, sex: null, details_source: 'known' },
      _manualVisibleVitalsKeys: getManualVisibleVitalsKeys(task),
    };
  }

  if (type === 'medication_admin') {
    const empty = createEmptyMarNote();
    const med = task
      ? buildMarMedFromTask(task)
      : {
          drug_name: '',
          name: '',
          dose: '',
          dose_amount: null,
          dose_unit: null,
          route: '',
          time_given: '',
          patient_response: '',
          response: '',
          adverse_reaction: false,
          adverse_reaction_details: null,
          confidence: 'high',
          pain_before: null,
          pain_after: null,
          bp_before: null,
          bp_systolic_before: null,
          bp_diastolic_before: null,
          pulse_before: null,
          blood_sugar_before: null,
          blood_sugar_after: null,
          inr: null,
          injection_site: null,
          infusion_rate: null,
          volume: null,
        };
    return {
      ...empty,
      medications_administered: [med],
      medications: [med],
    };
  }

  if (type === 'fluid_balance') {
    return createEmptyFluidBalanceNote();
  }

  if (type === 'care_plan') {
    return {
      note_type: 'care_plan',
      care_plan: {
        nursing_diagnosis: '',
        goals: '',
        interventions: [''],
        evaluation: '',
      },
      flags: [],
      patient: { name: null, room: null, age: null, sex: null, details_source: 'known' },
    };
  }

  return {
    note_type: 'progress_note',
    narrative: '',
    soap: {
      subjective: '',
      objective: '',
      assessment: '',
      plan: '',
    },
    vitals_extracted: {},
    flags: [],
    outstanding_tasks: [],
    patient: { name: null, room: null, age: null, sex: null, details_source: 'known' },
  };
}

function hasVitalValue(value) {
  return value != null && String(value).trim() !== '';
}

function isVitalKeyComplete(vitals, key) {
  if (key === 'bp') {
    return hasVitalValue(vitals.bp_systolic) && hasVitalValue(vitals.bp_diastolic);
  }
  return hasVitalValue(vitals[key]);
}

function getRequiredVitalKeys(note) {
  const vitals = note?.vitals || {};
  if (Array.isArray(note?._manualVisibleVitalsKeys) && note._manualVisibleVitalsKeys.length) {
    return note._manualVisibleVitalsKeys;
  }

  const keys = [];
  if (hasVitalValue(vitals.bp_systolic) || hasVitalValue(vitals.bp_diastolic)) keys.push('bp');
  if (hasVitalValue(vitals.pulse)) keys.push('pulse');
  if (hasVitalValue(vitals.temp_celsius)) keys.push('temp_celsius');
  if (hasVitalValue(vitals.o2_sat)) keys.push('o2_sat');
  if (hasVitalValue(vitals.rr)) keys.push('rr');
  if (hasVitalValue(vitals.pain)) keys.push('pain');
  if (hasVitalValue(vitals.weight_kg)) keys.push('weight_kg');
  if (hasVitalValue(vitals.blood_sugar)) keys.push('blood_sugar');
  return keys;
}

function medicationHasDose(med) {
  if (!med) return false;
  if (med.dose_amount != null && String(med.dose_amount).trim() !== '') return true;
  return hasText(String(med.dose || ''));
}

function isMedicationComplete(med) {
  if (!med) return false;
  const name = hasText(String(med.drug_name || med.name || ''));
  const route = hasText(String(med.route || ''));
  const time = hasText(String(med.time_given || ''));
  if (!(name && medicationHasDose(med) && route && time)) return false;
  return getMedicationRequiredExtraBlocker(med) == null;
}

export function noteHasDocumentationContent(type, note) {
  if (!note) return false;
  if (type === 'progress_note') {
    return progressNoteHasContent(note);
  }
  if (type === 'vitals') {
    const vitals = note.vitals || {};
    const requiredKeys = getRequiredVitalKeys(note);
    if (!requiredKeys.length) return false;
    return requiredKeys.every((key) => isVitalKeyComplete(vitals, key));
  }
  if (type === 'medication_admin') {
    const meds = note.medications_administered?.length
      ? note.medications_administered
      : note.medications || [];
    if (!meds.length) return false;
    return meds.every((med) => isMedicationComplete(med));
  }
  if (type === 'fluid_balance') {
    const inputs = note.fluid_inputs || [];
    const outputs = note.fluid_outputs || [];
    return [...inputs, ...outputs].some((entry) =>
      Object.values(entry || {}).some((value) => value != null && String(value).trim() !== '')
    );
  }
  if (type === 'care_plan') {
    const plan = note.care_plan || {};
    if (['nursing_diagnosis', 'goals', 'evaluation'].some((key) => hasText(plan[key]))) {
      return true;
    }
    return (plan.interventions || []).some((item) => hasText(item));
  }
  if (type === 'handover') {
    if (hasText(note.narrative)) return true;
    const sbar = note.sbar || {};
    return ['situation', 'background', 'assessment', 'recommendation'].some((key) =>
      hasText(sbar[key])
    );
  }
  return true;
}

export function createEmptyTaskDocumentationDraft(task) {
  const noteType = getDocumentationNoteType(task);
  const note = createEmptyNoteForType(noteType, task);
  const noteId = `task-doc-${task.id}`;
  return {
    noteId,
    encounterGroupId: noteId,
    encounterDateTime: new Date().toISOString(),
    noteType,
    generatedNotes: [{ type: noteType, note, error: null }],
    openaiNote: note,
    companionVitalsNote: null,
    cortiNote: null,
    cortiRaw: null,
    cortiFailed: true,
    openaiError: null,
    transcript: '',
    transcriptRaw: '',
    transcriptCleaned: '',
  };
}

/**
 * When reopening a task-doc session, fill missing MAR route/dose from the task
 * without wiping nurse edits that already set a route.
 */
export function refreshTaskDocumentationDraftFromTask(draft, task) {
  if (!task) return draft;
  if (!draft) return createEmptyTaskDocumentationDraft(task);
  if (getDocumentationNoteType(task) !== 'medication_admin') return draft;

  const freshMed = buildMarMedFromTask(task);

  const generatedNotes = (draft.generatedNotes || []).map((entry) => {
    if (entry.type !== 'medication_admin' || !entry.note) return entry;
    const meds = entry.note.medications_administered?.length
      ? entry.note.medications_administered
      : entry.note.medications || [];
    if (!meds.length) {
      return {
        ...entry,
        note: {
          ...entry.note,
          medications_administered: [freshMed],
          medications: [freshMed],
        },
      };
    }

    const nextMeds = meds.map((med, index) => {
      if (index !== 0) return med;
      const needsRoute = !String(med.route || '').trim();
      const needsDose = !String(med.dose || '').trim() && med.dose_amount == null;
      if (!needsRoute && !needsDose) return med;
      return {
        ...med,
        route: needsRoute ? freshMed.route : med.route,
        dose: med.dose || freshMed.dose,
        dose_amount: med.dose_amount ?? freshMed.dose_amount,
        dose_unit: med.dose_unit || freshMed.dose_unit,
      };
    });

    return {
      ...entry,
      note: {
        ...entry.note,
        medications_administered: nextMeds,
        medications: nextMeds,
      },
    };
  });

  const primary = generatedNotes.find((entry) => entry.type === 'medication_admin')?.note
    || draft.openaiNote;

  return {
    ...draft,
    generatedNotes,
    openaiNote: primary,
  };
}
